import { List, Map } from 'immutable';

import { getCurrentTrackFromActivePlaylist, getEnabledPlaybackControls } from '../utils';

import * as APSConstants from './constants';
import * as D from './deps';
import * as APSUtils from './utils';

/**
 * Function to get current APS item from state
 *
 * @param {object} appState - current state of the application
 * @returns {object}
 */
export function getCurrentItem(appState) {
  return appState.aps.get(APSConstants.APSKeys.CURRENT_ITEM);
}

/**
 * Function to get current APS item from state as an audioTrack for backwards compatibility, used by the audio domain
 *
 * @param {*} appState - current state of the application
 * @returns {object}
 */
export function getCurrentItemAudioTrack(appState) {
  const currentItem = getCurrentItem(appState);
  return currentItem?.size ? APSUtils.mapItemToAudioTrack(currentItem) : null;
}

/**
 * Function to get current APS item's elapsedTime from state
 *
 * @param {object} appState - current state of the application
 * @returns {number}
 */
export function getCurrentItemElapsedTime(appState) {
  const currentAudioId = D.getInDomain('playback.utils.getCurrentTrackToken', '')(appState);
  const currentAudioTime = D.getInDomain('playback.utils.getCurrentTrackTime', 0)(appState);
  return getCurrentItemPandoraId(appState) === currentAudioId ? currentAudioTime : 0;
}

/**
 * Function to get current APS item's itemId from state
 *
 * @param {object} appState - current state of the application
 * @returns {string}
 */
export function getCurrentItemItemId(appState) {
  return appState.aps.getIn([APSConstants.APSKeys.CURRENT_ITEM, 'itemId']);
}

/**
 * Function to get current APS item's narrative data from state
 *
 * @param {object} appState - current state of the application
 * @returns {object}
 */
export function getCurrentItemNarrative(appState) {
  return appState.aps.getIn([APSConstants.APSKeys.NARRATIVE]);
}

/**
 * Function to get current APS item's index from state
 *
 * @param {object} appState - current state of the application
 * @returns {string}
 */
export function getCurrentItemIndex(appState) {
  return appState.aps.getIn([APSConstants.APSKeys.CURRENT_ITEM, 'index'], null);
}

/**
 * Function to get current APS item's pandoraId from state
 *
 * @param {object} appState - current state of the application
 * @returns {string}
 */
export function getCurrentItemPandoraId(appState) {
  return appState.aps.getIn([APSConstants.APSKeys.CURRENT_ITEM, 'pandoraId']);
}

/**
 * Function to get current APS item's source's pandoraId from state (can be different from the source object's pandoraId if we've transitioned eg AU/PA)
 *
 * @param {object} appState - current state of the application
 * @returns {string}
 */
export function getCurrentItemSourceId(appState) {
  return appState.aps.getIn([APSConstants.APSKeys.CURRENT_ITEM, 'sourceId']);
}

/**
 * Function to get current APS item's lyric data from state
 *
 * @param {object} appState - current state of the application
 * @returns {string}
 */
export function getCurrentItemLyricSnippet(appState) {
  return appState.aps.getIn([APSConstants.APSKeys.LYRIC_DATA, 'lyricSnippet']);
}

/**
 * Function to get interactions from the state's current APS item
 *
 * @param {object} appState - current state of the application
 * @returns {Array}
 */
export function getInteractions(appState) {
  return appState.aps.getIn([APSConstants.APSKeys.CURRENT_ITEM, 'interactions'], []);
}

/**
 * Function to get next APS item from state
 *
 * @param {object} appState - current state of the application
 * @returns {object}
 */
export function getNextItem(appState) {
  return appState.aps.get(APSConstants.APSKeys.NEXT_ITEM, Map());
}

/**
 * Function to get an APS item from the current session
 *
 * @param {object} appState - current state of the application
 * @param {string} pandoraId - id for the item we want
 * @returns {Array}
 */
export function getSessionItem(appState, pandoraId) {
  return appState.aps.getIn([APSConstants.APSKeys.SESSION_ITEMS, pandoraId], Map());
}

/**
 * Function to get all APS item pandoraIds stored during the current session
 *
 * @param {object} appState - current state of the application
 * @returns {Array}
 */
export function getSessionItemPandoraIds(appState) {
  return appState.aps.get(APSConstants.APSKeys.SESSION_ITEMS).keys();
}

/**
 * Function to get an ordered list of APS item pandoraIds from the current session
 *
 * @param {object} appState - current state of the application
 * @returns {Array}
 */
export function getSessionList(appState) {
  return appState.aps.get(APSConstants.APSKeys.SESSION_LIST, List());
}

/**
 * Function to get current APS source data
 *
 * @param {object} appState - current state of the application
 * @returns {string}
 */
export function getSource(appState) {
  return appState.aps.get(APSConstants.APSKeys.SOURCE, Map());
}

/**
 * Function to get current APS source's pandoraId
 *
 * @param {object} appState - current state of the application
 * @returns {string}
 */
export function getSourcePandoraId(appState) {
  return appState.aps.getIn([APSConstants.APSKeys.SOURCE, 'pandoraId'], '');
}

/**
 * Function to get current APS source's repeat mode from state
 *
 * @param {object} appState - current state of the application
 * @returns {string}
 */
export function getSourceRepeatMode(appState) {
  return appState.aps.getIn([APSConstants.APSKeys.SOURCE, 'repeat'], null);
}

/**
 * Function to get current APS source's shuffle mode from state
 *
 * @param {object} appState - current state of the application
 * @returns {string}
 */
export function getSourceShuffleMode(appState) {
  return appState.aps.getIn([APSConstants.APSKeys.SOURCE, 'shuffle'], false);
}

/**
 * Function to get current APS source's sortOrder from state
 *
 * @param {object} appState - current state of the application
 * @returns {string}
 */
export function getSourceSortOrder(appState) {
  return appState.aps.getIn([APSConstants.APSKeys.SOURCE, 'sortOrder'], null);
}

/**
 * Function to get wether or not the itemId/sourceId combination is the current APS item
 *
 * @param {object} appState - current state of the application
 * @param {string} sourceId - the sourceId to check against
 * @param {string} itemId - the itemId to check against
 * @returns {string}
 */
export function isItemIdPlayingInSource(appState, { sourceId, itemId }) {
  const currentItem = getCurrentItem(appState);
  return (
    currentItem.get('sourceId') === sourceId && currentItem.get('itemId') === itemId.toString()
  );
}

export function shouldOfferSkipReward(state) {
  const track = getCurrentTrackFromActivePlaylist(state);
  const enabledControls = getEnabledPlaybackControls(state, track);
  return enabledControls.contains(D.Constants.Playback.PLAYBACK_CONTROLS.SKIP_REWARD);
}

export function shouldOfferReplayReward(state) {
  const track = getCurrentTrackFromActivePlaylist(state);
  const enabledControls = getEnabledPlaybackControls(state, track);
  return enabledControls.contains(D.Constants.Playback.PLAYBACK_CONTROLS.REPLAY_REWARD);
}
